import React, { RefObject, useEffect, useId, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { cx, useTranslation } from "utils";

import { OUTCOMES_TOUR_BAND_PX, OUTCOMES_TOUR_CARD_RADIUS_PX, OUTCOMES_TOUR_ELEMENT_ATTRIBUTE } from "consts";

import { Button, Icon, Typography } from "components";

import { useOutcomesTourGlide } from "pages/app/scenario-analysis/hooks/useOutcomesTourGlide";
import { useOutcomesTourTarget } from "pages/app/scenario-analysis/hooks/useOutcomesTourTarget";
import { OutcomesTourCard } from "pages/app/scenario-analysis/hooks/useSoilSightOutcomesTour";
import { useStepAwayOnOutsidePress } from "pages/app/scenario-analysis/hooks/useStepAwayOnOutsidePress";
import { toWholePixels } from "pages/app/scenario-analysis/utils/outcomesTourLayout";

// The barrel's Button forwards no ref, so Next is found by id inside the card.
const NEXT_BUTTON_ID = "outcomes-tour-next";

const TOUR_ELEMENT_PROPS = { [OUTCOMES_TOUR_ELEMENT_ATTRIBUTE]: "" };

// Shared with the SCSS, so the band's width and the card's corners cannot drift from the placement maths.
const TOUR_CSS_VARIABLES = {
  "--outcomes-tour-band": toWholePixels(OUTCOMES_TOUR_BAND_PX),
  "--outcomes-tour-card-radius": toWholePixels(OUTCOMES_TOUR_CARD_RADIUS_PX),
} as React.CSSProperties;

export interface SoilSightOutcomesTourProps extends OutcomesTourCard {
  /** Where the page's tour targets live. */
  rootRef: RefObject<HTMLElement>;
  /** The sticky tab and settings bar: areas scroll clear of it and the band is never drawn over it. */
  stickyBarRef: RefObject<HTMLElement>;
}

/** One tour step: a band of yellow around the area and a non-modal card beside it. Nothing is dimmed, and clicks in the area pass through. */
export const SoilSightOutcomesTour = ({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onBack,
  onFinish,
  onLastAreaUsed,
  onDismiss,
  onStepAway,
  rootRef,
  stickyBarRef,
}: SoilSightOutcomesTourProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const titleId = useId();
  const textId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const isLastStep = stepIndex === totalSteps - 1;
  const finishesOnUse = isLastStep && Boolean(step.finishesOnUse);

  const { target, hasGivenUpOnTarget, hasLostTarget } = useOutcomesTourTarget(rootRef, step);
  const { isShown, isOnBand } = useOutcomesTourGlide({ step, target, hasGivenUpOnTarget, hasLostTarget, cardRef, highlightRef, stickyBarRef });
  useStepAwayOnOutsidePress({ cardRef, target, isOnBand, onStepAway });

  // Until placed, the card is invisible, so it is also kept out of focus and the accessibility tree. React 18 has no `inert` prop.
  useLayoutEffect(() => {
    cardRef.current?.toggleAttribute("inert", !isShown);
  }, [isShown]);

  useEffect(() => {
    if (isShown) cardRef.current?.querySelector<HTMLElement>(`#${NEXT_BUTTON_ID}`)?.focus({ preventScroll: true });
  }, [step, isShown]);

  // Capture, so the tour has closed before the click opens what it asked for.
  useEffect(() => {
    if (!target || !finishesOnUse) return undefined;
    target.addEventListener("click", onLastAreaUsed, true);
    return () => target.removeEventListener("click", onLastAreaUsed, true);
  }, [target, finishesOnUse, onLastAreaUsed]);

  // Only while focus is in the card, so typing or using a control elsewhere is never hijacked.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isShown) return;
    if (event.key === "Escape") {
      event.preventDefault();
      onDismiss();
    } else if (event.key === "ArrowRight" && !isLastStep) {
      event.preventDefault();
      onNext();
    } else if (event.key === "ArrowLeft" && stepIndex > 0) {
      event.preventDefault();
      onBack();
    }
  };

  return createPortal(
    <>
      <div
        ref={highlightRef}
        className={cx("outcomes-tour-highlight", !isShown && "outcomes-tour-hidden")}
        style={TOUR_CSS_VARIABLES}
        aria-hidden
        data-testid="outcomes-tour-highlight"
        {...TOUR_ELEMENT_PROPS}
      />

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-hidden={!isShown || undefined}
        aria-labelledby={titleId}
        aria-describedby={textId}
        className={cx("outcomes-tour-card", !isShown && "outcomes-tour-hidden")}
        style={TOUR_CSS_VARIABLES}
        onKeyDown={handleKeyDown}
        data-testid="outcomes-tour-card"
        {...TOUR_ELEMENT_PROPS}
      >
        <div className="outcomes-tour-card-header">
          <Typography variant="paragraph-xs" className="outcomes-tour-card-step">
            {t("outcomesTour.stepIndicator", { current: stepIndex + 1, total: totalSteps })}
          </Typography>
          <Button
            type="text"
            size="small"
            className="outcomes-tour-card-close"
            onClick={onDismiss}
            aria-label={t("outcomesTour.close")}
            icon={<Icon iconName="tabler/x" size="s" />}
            dataTestId="outcomes-tour-close"
          />
        </div>

        {/* Focus stays on Next between steps, so the new step's words are announced from here. */}
        <div className="outcomes-tour-card-body" aria-live="polite">
          <Typography variant="paragraph-l" id={titleId} className="outcomes-tour-card-title">
            {t(`outcomesTour.steps.${step.id}.title`)}
          </Typography>
          <Typography variant="paragraph-m" id={textId} className="outcomes-tour-card-text">
            {t(`outcomesTour.steps.${step.id}.text`)}
          </Typography>
        </div>

        <div className="outcomes-tour-card-actions">
          {stepIndex > 0 ? (
            <Button
              type="text"
              size="small"
              className="outcomes-tour-card-back"
              icon={<Icon iconName="tabler/arrow-left" size="s" />}
              onClick={onBack}
              dataTestId="outcomes-tour-back"
            >
              {t("outcomesTour.back")}
            </Button>
          ) : null}
          <Button
            type="primary"
            size="small"
            className="outcomes-tour-card-next"
            id={NEXT_BUTTON_ID}
            onClick={isLastStep ? onFinish : onNext}
            icon={isLastStep ? undefined : <Icon iconName="tabler/arrow-right" size="s" color="white" />}
            dataTestId="outcomes-tour-next"
          >
            {isLastStep ? t("outcomesTour.gotIt") : t("outcomesTour.next")}
          </Button>
        </div>
      </div>
    </>,
    document.body
  );
};
